import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import Layout from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { DollarSign, AlertTriangle, TrendingUp, FileText, Calendar, RefreshCw } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
import { toast } from 'sonner';
import DateFilter from '../components/DateFilter';
import { parseDateFilterFromURL, serializeDateFilterToURL, buildAPIParams } from '../utils/dateFilterUtils';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Dashboard = ({ user }) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [stats, setStats] = useState(null);
  const [invoices, setInvoices] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  // Parse date filter from URL, default to This Month with Due Date basis
  const [dateFilter, setDateFilter] = useState(() => parseDateFilterFromURL(searchParams));

  useEffect(() => {
    fetchStats();
  }, [dateFilter]);

  const handleDateFilterChange = (newFilter) => {
    setDateFilter(newFilter);
    // Update URL params
    setSearchParams(serializeDateFilterToURL(newFilter));
  };

  const fetchStats = async (showToast = false) => {
    if (showToast) setRefreshing(true);
    try {
      const params = buildAPIParams(dateFilter);
      const statsUrl = `${API}/dashboard/stats${params ? `?${params}` : ''}`;
      const invoicesUrl = `${API}/invoices${params ? `?${params}` : ''}`;
      const clientsUrl = `${API}/clients`;

      const [statsRes, invoicesRes, clientsRes] = await Promise.all([
        axios.get(statsUrl),
        axios.get(invoicesUrl),
        axios.get(clientsUrl)
      ]);

      setStats(statsRes.data);
      setInvoices(invoicesRes.data);
      setClients(clientsRes.data);

      if (showToast) {
        toast.success('Dashboard refreshed');
      }
    } catch (error) {
      console.error('Error fetching stats:', error);
      console.error('Error response:', error.response?.data);
      if (showToast) {
        const errorMsg = error.response?.data?.detail || 'Failed to refresh dashboard';
        toast.error(errorMsg);
      }
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  if (loading) {
    return (
      <Layout user={user}>
        <div className="flex items-center justify-center h-full">
          <div className="text-lg">Loading...</div>
        </div>
      </Layout>
    );
  }

  // Calculate invoices due in next 30 days
  const calculateDueNext30Days = () => {
    const now = new Date();
    const next30Days = new Date(now.getTime() + 30 * 24 * 60 * 60 * 1000);

    return invoices.reduce((total, inv) => {
      if (inv.status === 'pending' || inv.status === 'partial') {
        const dueDate = new Date(inv.due_date);
        if (dueDate >= now && dueDate <= next30Days) {
          const outstanding = inv.amount - (inv.paid_amount || 0);
          return total + outstanding;
        }
      }
      return total;
    }, 0);
  };

  // Get top overdue invoices
  const getTopOverdueInvoices = () => {
    const now = new Date();
    return invoices
      .filter(inv => {
        const dueDate = new Date(inv.due_date);
        return (inv.status === 'pending' || inv.status === 'partial' || inv.status === 'overdue') && dueDate < now;
      })
      .map(inv => ({
        ...inv,
        outstanding: inv.amount - (inv.paid_amount || 0)
      }))
      .sort((a, b) => b.outstanding - a.outstanding)
      .slice(0, 5);
  };

  // Get customers with highest exposure
  const getCustomersWithHighestExposure = () => {
    const customerExposure = {};

    invoices.forEach(inv => {
      if (inv.status === 'pending' || inv.status === 'partial' || inv.status === 'overdue') {
        const outstanding = inv.amount - (inv.paid_amount || 0);
        if (!customerExposure[inv.client_id]) {
          const client = clients.find(c => c.id === inv.client_id);
          customerExposure[inv.client_id] = {
            client_id: inv.client_id,
            client_name: client?.company || client?.name || 'Unknown',
            total_outstanding: 0,
            invoice_count: 0
          };
        }
        customerExposure[inv.client_id].total_outstanding += outstanding;
        customerExposure[inv.client_id].invoice_count += 1;
      }
    });

    return Object.values(customerExposure)
      .sort((a, b) => b.total_outstanding - a.total_outstanding)
      .slice(0, 5);
  };

  const agingData = stats?.aging ? [
    { name: '0-30 Days', value: stats.aging['0-30'] },
    { name: '31-60 Days', value: stats.aging['31-60'] },
    { name: '61-90 Days', value: stats.aging['61-90'] },
    { name: '90+ Days', value: stats.aging['90+'] },
  ] : [];

  const statusData = [
    { name: 'Pending', value: invoices.filter(inv => inv.status === 'pending').length, fill: '#F59E0B' },
    { name: 'Paid', value: invoices.filter(inv => inv.status === 'paid').length, fill: '#10B981' },
    { name: 'Overdue', value: invoices.filter(inv => inv.status === 'overdue').length, fill: '#EF4444' },
    { name: 'Partial', value: invoices.filter(inv => inv.status === 'partial').length, fill: '#3B82F6' },
  ].filter(item => item.value > 0);

  const dueNext30Days = calculateDueNext30Days();
  const topOverdueInvoices = getTopOverdueInvoices();
  const topCustomers = getCustomersWithHighestExposure();

  const scorecards = [
    {
      title: 'Outstanding Invoice Balance',
      value: `$${stats?.total_outstanding?.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }) || '0.00'}`,
      icon: DollarSign,
      color: 'text-slate-900',
      bgColor: 'bg-slate-50',
      testId: 'metric-outstanding'
    },
    {
      title: 'Overdue Invoice Balance',
      value: `$${stats?.total_overdue?.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }) || '0.00'}`,
      icon: AlertTriangle,
      color: 'text-orange-600',
      bgColor: 'bg-orange-50',
      testId: 'metric-overdue'
    },
    {
      title: 'Invoices Due Next 30 Days',
      value: `$${dueNext30Days.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
      icon: Calendar,
      color: 'text-blue-600',
      bgColor: 'bg-blue-50',
      testId: 'metric-due-next-30'
    },
    {
      title: 'Total Invoices',
      value: stats?.total_invoices || '0',
      icon: FileText,
      color: 'text-slate-700',
      bgColor: 'bg-slate-50',
      testId: 'metric-total-invoices'
    },
  ];

  return (
    <Layout user={user}>
      <div className="p-4 md:p-8" data-testid="dashboard-page">
        {/* Header */}
        <div className="mb-6 md:mb-8">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-3">
            <div>
              <h1 className="font-heading text-2xl md:text-4xl text-slate-900 mb-1 md:mb-2">Dashboard</h1>
              <p className="text-sm md:text-base text-slate-600">
                Metrics based on invoice and recorded payment data. Accounting and cash forecasting integrations coming soon.
              </p>
            </div>
            <Button
              onClick={() => fetchStats(true)}
              disabled={refreshing}
              variant="outline"
              className="rounded-none border-slate-300 w-full sm:w-auto"
            >
              <RefreshCw className={`h-4 w-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
              {refreshing ? 'Refreshing...' : 'Refresh'}
            </Button>
          </div>

          {/* Date Filter */}
          <DateFilter
            preset={dateFilter.preset}
            basis={dateFilter.basis}
            startDate={dateFilter.startDate}
            endDate={dateFilter.endDate}
            onChange={handleDateFilterChange}
          />
        </div>

        {/* Row 1: Scorecards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6 md:mb-8">
          {scorecards.map((scorecard) => {
            const Icon = scorecard.icon;
            return (
              <Card
                key={scorecard.title}
                data-testid={scorecard.testId}
                className="border border-slate-200 rounded-none shadow-none"
              >
                <CardContent className="p-4 md:p-6">
                  <div className="flex items-start justify-between">
                    <div className="flex-1 min-w-0">
                      <p className="text-xs md:text-sm text-slate-500 mb-2 uppercase tracking-wide font-medium">
                        {scorecard.title}
                      </p>
                      <p className="font-mono-data text-2xl md:text-3xl font-bold text-slate-900">
                        {scorecard.value}
                      </p>
                    </div>
                    <div className={`${scorecard.bgColor} p-2 md:p-3 rounded-sm flex-shrink-0`}>
                      <Icon className={`h-5 w-5 md:h-6 md:w-6 ${scorecard.color}`} strokeWidth={1.5} />
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* Row 2: Charts */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 md:gap-6 mb-6 md:mb-8">
          {/* Invoice Aging (by Due Date) */}
          <Card className="border border-slate-200 rounded-none shadow-none" data-testid="aging-chart">
            <CardHeader className="border-b border-slate-100 p-4 md:p-6 bg-slate-50/50">
              <CardTitle className="font-heading text-lg md:text-xl">Invoice Aging (by Due Date)</CardTitle>
              <p className="text-xs md:text-sm text-slate-600 mt-1">Outstanding by days overdue</p>
            </CardHeader>
            <CardContent className="p-4 md:p-6">
              {agingData.some(d => d.value > 0) ? (
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={agingData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                    <XAxis dataKey="name" stroke="#64748B" style={{ fontSize: '10px' }} tick={{ fontSize: 10 }} />
                    <YAxis stroke="#64748B" style={{ fontSize: '10px' }} tick={{ fontSize: 10 }} />
                    <Tooltip
                      contentStyle={{
                        backgroundColor: '#FFFFFF',
                        border: '1px solid #E2E8F0',
                        borderRadius: '0',
                        fontFamily: 'JetBrains Mono, monospace',
                        fontSize: '12px'
                      }}
                      formatter={(value) => `$${value.toLocaleString()}`}
                    />
                    <Bar dataKey="value" fill="#0F172A" />
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <div className="h-[300px] flex items-center justify-center text-slate-500">
                  <p>No overdue invoices</p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Invoice Status Breakdown */}
          <Card className="border border-slate-200 rounded-none shadow-none" data-testid="status-chart">
            <CardHeader className="border-b border-slate-100 p-4 md:p-6 bg-slate-50/50">
              <CardTitle className="font-heading text-lg md:text-xl">Invoice Status Breakdown</CardTitle>
              <p className="text-xs md:text-sm text-slate-600 mt-1">Distribution by status</p>
            </CardHeader>
            <CardContent className="p-4 md:p-6">
              {statusData.length > 0 ? (
                <ResponsiveContainer width="100%" height={300}>
                  <PieChart>
                    <Pie
                      data={statusData}
                      cx="50%"
                      cy="50%"
                      labelLine={false}
                      label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                      outerRadius={100}
                      dataKey="value"
                    >
                      {statusData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.fill} />
                      ))}
                    </Pie>
                    <Tooltip
                      contentStyle={{
                        backgroundColor: '#FFFFFF',
                        border: '1px solid #E2E8F0',
                        borderRadius: '0'
                      }}
                    />
                  </PieChart>
                </ResponsiveContainer>
              ) : (
                <div className="h-[300px] flex items-center justify-center text-slate-500">
                  <p>No invoice data</p>
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Row 3: Actionable Tables */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 md:gap-6">
          {/* Top Overdue Invoices */}
          <Card className="border border-slate-200 rounded-none shadow-none" data-testid="top-overdue">
            <CardHeader className="border-b border-slate-100 p-4 md:p-6 bg-slate-50/50">
              <CardTitle className="font-heading text-lg md:text-xl">Top Overdue Invoices</CardTitle>
              <p className="text-xs md:text-sm text-slate-600 mt-1">Highest outstanding overdue amounts</p>
            </CardHeader>
            <CardContent className="p-0">
              {topOverdueInvoices.length > 0 ? (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-slate-50/50 border-b border-slate-200">
                      <tr>
                        <th className="text-left text-xs font-medium text-slate-600 uppercase tracking-wide px-4 py-3">Invoice #</th>
                        <th className="text-left text-xs font-medium text-slate-600 uppercase tracking-wide px-4 py-3">Client</th>
                        <th className="text-right text-xs font-medium text-slate-600 uppercase tracking-wide px-4 py-3">Outstanding</th>
                        <th className="text-right text-xs font-medium text-slate-600 uppercase tracking-wide px-4 py-3">Due Date</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-200">
                      {topOverdueInvoices.map((invoice) => {
                        const client = clients.find(c => c.id === invoice.client_id);
                        return (
                          <tr key={invoice.invoice_number} className="hover:bg-slate-50">
                            <td className="px-4 py-3 text-sm font-medium text-slate-900">{invoice.invoice_number}</td>
                            <td className="px-4 py-3 text-sm text-slate-700">{client?.company || client?.name || 'Unknown'}</td>
                            <td className="px-4 py-3 text-sm text-right font-mono-data font-semibold text-orange-600">
                              ${invoice.outstanding.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                            </td>
                            <td className="px-4 py-3 text-sm text-right text-slate-600">
                              {new Date(invoice.due_date).toLocaleDateString()}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="p-8 text-center text-slate-500">
                  <p>No overdue invoices</p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Customers with Highest Exposure */}
          <Card className="border border-slate-200 rounded-none shadow-none" data-testid="customer-exposure">
            <CardHeader className="border-b border-slate-100 p-4 md:p-6 bg-slate-50/50">
              <CardTitle className="font-heading text-lg md:text-xl">Customers with Highest Exposure</CardTitle>
              <p className="text-xs md:text-sm text-slate-600 mt-1">Total outstanding by customer</p>
            </CardHeader>
            <CardContent className="p-0">
              {topCustomers.length > 0 ? (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-slate-50/50 border-b border-slate-200">
                      <tr>
                        <th className="text-left text-xs font-medium text-slate-600 uppercase tracking-wide px-4 py-3">Customer</th>
                        <th className="text-center text-xs font-medium text-slate-600 uppercase tracking-wide px-4 py-3">Invoices</th>
                        <th className="text-right text-xs font-medium text-slate-600 uppercase tracking-wide px-4 py-3">Total Outstanding</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-200">
                      {topCustomers.map((customer) => (
                        <tr key={customer.client_id} className="hover:bg-slate-50">
                          <td className="px-4 py-3 text-sm font-medium text-slate-900">{customer.client_name}</td>
                          <td className="px-4 py-3 text-sm text-center text-slate-700">{customer.invoice_count}</td>
                          <td className="px-4 py-3 text-sm text-right font-mono-data font-semibold text-slate-900">
                            ${customer.total_outstanding.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="p-8 text-center text-slate-500">
                  <p>No outstanding customer balances</p>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </Layout>
  );
};

export default Dashboard;